import { SHIP_RADIUS, WORLD_WIDTH, WORLD_HEIGHT } from 'asteroids-shared/constants'
import { wrappedDelta } from 'asteroids-shared/helpers'
import { drawShip } from './ShipRenderer.js'

export const SHIP_INDICATOR_SCALE = .5
export const SHIP_INDICATOR_ALPHA = .35

// Minimum inset from viewport edges; keeps forEachVisibleCopy from wrapping the ghost ship.
export function shipIndicatorEdgePadding() {
  return Math.ceil(SHIP_RADIUS * SHIP_INDICATOR_SCALE + 10)
}

// Ships that may get an off-screen edge indicator (e.g. other alive players).
export function indicatorTargets(view, shipId) {
  return view.ships.filter((s) => s.id !== shipId && s.alive)
}

// Vector from (fromX, fromY) to (toX, toY), shortest on a torus when enabled.
export function indicatorWorldVector(fromX, fromY, toX, toY, toroidalMap = true) {
  if (!toroidalMap) {
    return {
      dx: toX - fromX,
      dy: toY - fromY
    }
  }
  return {
    dx: wrappedDelta(toX, fromX, WORLD_WIDTH),
    dy: wrappedDelta(toY, fromY, WORLD_HEIGHT)
  }
}

// Rotation (degrees) so ship nose points toward (dx, dy) (matches drawShip nose at local (0, -15)).
export function indicatorPointerRotationDeg(dx, dy) {
  return Math.atan2(dx, -dy) * 180 / Math.PI
}

/**
 * Ray from viewport center along (udx, udy) to first hit on the padded inner rect.
 * @returns {{ x: number, y: number } | null}
 */
export function indicatorScreenEdgePoint(udx, udy, pad, viewW, viewH) {
  const cx = viewW / 2
  const cy = viewH / 2
  const xmin = pad
  const xmax = viewW - pad
  const ymin = pad
  const ymax = viewH - pad
  const EPS = 1e-9
  let bestT = Infinity

  const tryT = (t) => {
    if (t <= EPS) return
    const x = cx + t * udx
    const y = cy + t * udy
    if (x >= xmin - 1e-6 && x <= xmax + 1e-6 && y >= ymin - 1e-6 && y <= ymax + 1e-6) {
      bestT = Math.min(bestT, t)
    }
  }

  if (Math.abs(udx) > EPS) {
    tryT((xmin - cx) / udx)
    tryT((xmax - cx) / udx)
  }
  if (Math.abs(udy) > EPS) {
    tryT((ymin - cy) / udy)
    tryT((ymax - cy) / udy)
  }

  if (bestT === Infinity) return null
  return { x: cx + bestT * udx, y: cy + bestT * udy }
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ screenX: number, screenY: number, angleDeg: number, color: string, camera: { x: number, y: number, viewW: number, viewH: number } }} opts
 */
export function drawShipIndicator(ctx, { screenX, screenY, angleDeg, color, camera }) {
  ctx.save()
  ctx.globalAlpha = SHIP_INDICATOR_ALPHA
  ctx.translate(screenX, screenY)
  ctx.scale(SHIP_INDICATOR_SCALE, SHIP_INDICATOR_SCALE)
  ctx.translate(-screenX, -screenY)
  drawShip(ctx, {
    position: { x: camera.x + screenX, y: camera.y + screenY },
    rotation: angleDeg,
    radius: SHIP_RADIUS,
    alive: true,
    color
  }, camera)
  ctx.restore()
}
